/**
 * The products' names on every screen, notice, e-mail and error text, from one place per edition (v0.15e.2,
 * D-217; CLAUDE.md "Ürün adları"). The kurumsal edition sells "Chat and Cowork" (the chat, profile `chat`, the
 * chat. address, 8480) and "Code" (profile `code`, the code. address, 8481); the bireysel edition sells only
 * "Chat" (a later round takes Code out of it; until then it keeps the kurumsal name). Internal names never change:
 * the profiles, the license's products `companygpt` and `companycode`, the addresses, `DBRAIN_*`.
 *
 * A Turkish text needs a case ending after a name, written after an apostrophe and chosen by how the name is said:
 * "Chat and Cowork" ends in "kovörk" ("Chat and Cowork'e", "…'te"), "Code" in "kod" ("Code'a", "…'da"), "Chat" in
 * "çet" ("Chat'e", "…'te"). The web's i18n has these as default variables (`chatProduct`, `chatProductTo`,
 * `chatProductIn`, `chatProductFrom`, and the same for `code`); the phone's texts too. Change the forms together.
 */
export type ProductEdition = 'bireysel' | 'kurumsal';
export type ProductKey = 'chat' | 'code';

export interface ProductName {
  /** "Chat and Cowork" */
  name: string;
  /** Turkish dative: "Chat and Cowork'e geç" */
  to: string;
  /** Turkish locative: "Chat and Cowork'te çalışır" */
  in: string;
  /** Turkish ablative: "Chat and Cowork'ten açın" */
  from: string;
}

const CODE: ProductName = { name: 'Code', to: "Code'a", in: "Code'da", from: "Code'dan" };

export const PRODUCT_NAMES: Record<ProductEdition, Record<ProductKey, ProductName>> = {
  kurumsal: {
    chat: { name: 'Chat and Cowork', to: "Chat and Cowork'e", in: "Chat and Cowork'te", from: "Chat and Cowork'ten" },
    code: CODE,
  },
  bireysel: {
    chat: { name: 'Chat', to: "Chat'e", in: "Chat'te", from: "Chat'ten" },
    code: CODE,
  },
};

export function productNames(edition: ProductEdition | undefined): Record<ProductKey, ProductName> {
  return PRODUCT_NAMES[edition ?? 'kurumsal'];
}

/** The i18n default variables of an edition: {{chatProduct}}, {{chatProductTo}}, {{chatProductIn}}, {{chatProductFrom}}, and the same for code. */
export function productVariables(edition: ProductEdition | undefined): Record<string, string> {
  const names = productNames(edition);
  const out: Record<string, string> = {};
  for (const key of ['chat', 'code'] as const) {
    const n = names[key];
    out[`${key}Product`] = n.name;
    out[`${key}ProductTo`] = n.to;
    out[`${key}ProductIn`] = n.in;
    out[`${key}ProductFrom`] = n.from;
  }
  return out;
}
